import type { z } from 'zod'
import { queryClient, sessionKey } from './query'

/**
 * Erro que a interface sabe mostrar.
 *
 * `code` é a etiqueta estável que a API manda e que a tela pode comparar;
 * `message` é o texto em português que já vem pronto para aparecer. Toda
 * falha de `api()` chega assim, inclusive a que nasce do próprio navegador,
 * para que nenhuma tela precise saber a diferença entre servidor fora do ar
 * e resposta malformada.
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  /**
   * O resto do corpo do erro. Um 409 de horário ocupado vem com as
   * alternativas, e uma explicação que mudou vem com a nova: sem guardar isso
   * a tela só saberia dizer não.
   */
  readonly details: Record<string, unknown>

  constructor(
    status: number,
    code: string,
    message: string,
    details: Record<string, unknown> = {},
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

/**
 * Para onde o 401 manda quem estava logado. Trocável nos testes. Leva o
 * caminho atual em `?volta=` para a pessoa cair onde estava depois de entrar.
 */
let onUnauthorized = (): void => {
  const { pathname, search } = window.location
  const destino = pathname.startsWith('/portal') ? '/portal/entrar' : '/app/entrar'
  if (pathname === destino) return
  window.location.assign(`${destino}?volta=${encodeURIComponent(pathname + search)}`)
}

export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler
}

export interface ApiOptions<T> {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  /** Schema da resposta. Sem ele o retorno é `undefined`. */
  schema?: z.ZodType<T>
  signal?: AbortSignal
  /** Troca o prazo padrão. A IA leva mais de 20 s numa sessão longa. */
  timeoutMs?: number
}

type ErrorBody = Record<string, unknown>

/** Acima disso o pedido desiste. Rede ruim de consultório segura um fetch por minutos. */
export const API_TIMEOUT_MS = 20_000

function erroDeRede(): ApiError {
  // `onLine` falso é certeza de estar sem rede; verdadeiro não garante nada,
  // então nesse caso a culpa fica com o servidor.
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return new ApiError(0, 'offline', 'Sem conexão com a internet.')
  }
  return new ApiError(0, 'servidor-fora', 'Não foi possível falar com o servidor.')
}

function tempoEsgotado(): ApiError {
  return new ApiError(0, 'tempo-esgotado', 'O servidor demorou demais para responder.')
}

/**
 * Uma chamada à API.
 *
 * Credencial `same-origin`: o cookie de sessão nunca sai para outra origem.
 * A resposta só é devolvida depois de passar pelo schema, então nenhuma tela
 * lida com dado não validado. Falha de rede sai como `ApiError` de status 0:
 * `offline`, `servidor-fora` ou `tempo-esgotado`.
 */
export async function api<T = undefined>(path: string, options: ApiOptions<T> = {}): Promise<T> {
  const { method = 'GET', body, schema, signal, timeoutMs = API_TIMEOUT_MS } = options

  // Um controlador próprio junta o cancelamento de quem chamou com o prazo.
  // `AbortSignal.any` faria o mesmo, mas falta em Safari anterior ao 17.4.
  const controller = new AbortController()
  let esgotou = false
  const timer = setTimeout(() => {
    esgotou = true
    controller.abort()
  }, timeoutMs)
  const repassar = (): void => controller.abort()
  if (signal?.aborted) controller.abort()
  else signal?.addEventListener('abort', repassar, { once: true })

  try {
    return await request(path, method, body, schema, controller.signal)
  } catch (cause) {
    if (esgotou) throw tempoEsgotado()
    throw cause
  } finally {
    clearTimeout(timer)
    signal?.removeEventListener('abort', repassar)
  }
}

async function request<T>(
  path: string,
  method: NonNullable<ApiOptions<T>['method']>,
  body: unknown,
  schema: z.ZodType<T> | undefined,
  signal: AbortSignal,
): Promise<T> {
  // As chaves opcionais entram por espalhamento: com
  // `exactOptionalPropertyTypes` passar `undefined` explícito não é a mesma
  // coisa que não passar, e o `fetch` recusa.
  const init: RequestInit = {
    method,
    credentials: 'same-origin',
    ...(body === undefined
      ? {}
      : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
    signal,
  }

  let response: Response
  try {
    response = await fetch(path.startsWith('/') ? path : `/api/${path}`, init)
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw erroDeRede()
  }

  if (response.status === 401) {
    // A sessão morreu enquanto a tela estava aberta. Invalida antes de sair
    // para que a próxima montagem não leia o usuário antigo do cache.
    await queryClient.invalidateQueries({ queryKey: sessionKey })
    onUnauthorized()
    throw new ApiError(401, 'sem-sessao', 'Sua sessão expirou. Entre de novo.')
  }

  if (!response.ok) {
    // Proxy fora do ar responde HTML; aí o corpo vira `{}` e quem explica é
    // o status, em `lib/erros.ts`.
    const parsed: ErrorBody = await response.json().catch(() => ({}))
    const code = typeof parsed.code === 'string' ? parsed.code : 'erro'
    // Parte das rotas ainda responde `{ error: '...' }` em vez de `message`.
    const message =
      typeof parsed.message === 'string'
        ? parsed.message
        : typeof parsed.error === 'string'
          ? parsed.error
          : 'Não deu para completar a operação.'
    const { code: _code, message: _message, error: _error, ...details } = parsed
    throw new ApiError(response.status, code, message, details)
  }

  if (schema === undefined) return undefined as T

  const payload: unknown = await response.json().catch((cause: unknown) => {
    if (signal.aborted) throw cause
    throw new ApiError(response.status, 'resposta-invalida', 'O servidor respondeu fora do padrão.')
  })

  const result = schema.safeParse(payload)
  if (!result.success) {
    throw new ApiError(response.status, 'resposta-invalida', 'O servidor respondeu fora do padrão.')
  }
  return result.data
}
